import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface NavItem {
  to: string;
  label: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Grupos lógicos do menu (dropdowns)
const GROUPS: NavGroup[] = [
  {
    label: 'Ranking Lista',
    items: [
      { to: '/ranking-analysis', label: 'Ranking Analysis' },
      { to: '/performance-comparison', label: 'Performance Comparison' },
      { to: '/ranking-simulator', label: '🎛️ Ranking Simulator' },
      { to: '/ranking-health', label: '🏥 Ranking Health' },
    ],
  },
  {
    label: 'Preços',
    items: [
      { to: '/price-changes', label: 'Mudanças de Preço' },
      { to: '/products', label: 'Lista de Produtos' },
    ],
  },
  {
    // Funcionalidades que alimentam o site e a API externa
    label: 'Site',
    items: [
      { to: '/basket-analysis', label: '🛒 Comprados em Conjunto' },
      { to: '/semantic-search', label: '🧭 Produtos Semelhantes' },
    ],
  },
  {
    label: 'Replenishment',
    items: [
      { to: '/replenishment', label: '🏬 Hit Rate das Lojas' },
      { to: '/replenishment/produtos-em-falta', label: '🔍 Produtos em falta' },
      { to: '/replenishment/catalogacao', label: '🗂 Catalogação' },
      { to: '/replenishment/promocoes', label: '🏷️ Promoções' },
    ],
  },
  {
    label: 'Stock',
    items: [
      { to: '/stock-forecast', label: 'Stock Forecast' },
      { to: '/stock/transferencias', label: '🔄 Transferências armazém ↔ lojas' },
      { to: '/stock/camioes', label: '🚚 Volumetria por camião' },
    ],
  },
];

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  // Sem sessão (ou no login): navbar mínima.
  if (!user) {
    return (
      <nav className="navbar navbar-dark bg-primary">
        <div className="container-fluid">
          <span className="navbar-brand"><strong>Price History Platform</strong></span>
        </div>
      </nav>
    );
  }

  const isActive = (path: string) => location.pathname === path;
  const groupActive = (group: NavGroup) => group.items.some((i) => isActive(i.to));

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
      <div className="container-fluid">
        <Link className="navbar-brand" to="/">
          <strong>Price History Platform</strong>
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav me-auto">
            <li className="nav-item">
              <Link className={`nav-link ${isActive('/') ? 'active' : ''}`} to="/">
                Home
              </Link>
            </li>

            {GROUPS.map((group) => (
              <li className="nav-item dropdown" key={group.label}>
                <a
                  className={`nav-link dropdown-toggle ${groupActive(group) ? 'active' : ''}`}
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {group.label}
                </a>
                <ul className="dropdown-menu">
                  {group.items.map((item) => (
                    <li key={item.to}>
                      <Link
                        className={`dropdown-item ${isActive(item.to) ? 'active' : ''}`}
                        to={item.to}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}

            <li className="nav-item">
              <Link
                className={`nav-link ${isActive('/sales-monitoring') ? 'active' : ''}`}
                to="/sales-monitoring"
              >
                <i className="bi bi-exclamation-triangle-fill me-1"></i>
                Monitorização
              </Link>
            </li>
          </ul>

          <ul className="navbar-nav ms-auto align-items-lg-center">
            {user.is_staff && (
              <li className="nav-item">
                <Link
                  className={`nav-link ${isActive('/api-keys') ? 'active' : ''}`}
                  to="/api-keys"
                >
                  ⚙️ API Keys
                </Link>
              </li>
            )}
            <li className="nav-item">
              <span className="navbar-text me-2">{user.username}</span>
            </li>
            <li className="nav-item">
              <button className="btn btn-sm btn-outline-light" onClick={handleLogout}>
                Sair
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
